/**
 * Relatórios da área de BI (#557): o ranking de vendas.
 *
 * ── Venda é a ENTRADA numa etapa final marcada como ganho ─────────────────
 *
 * A mesma base do bloco "Resultado" do painel (057): a oportunidade conta no
 * período em que entrou numa etapa final classificada como ganho, uma vez só,
 * pela última entrada numa etapa final dentro do período. Oportunidade que foi
 * ganha e depois movida para perda no mesmo período não conta como venda. O
 * ranking e o painel somam o mesmo conjunto: um número diferente nos dois
 * lugares seria o primeiro motivo para o gestor desconfiar dos dois.
 *
 * ── O critério é o VALOR vendido (decidido em 28/09/2026) ─────────────────
 *
 * A quantidade aparece ao lado, só como informação. Venda sem valor conta na
 * quantidade e soma zero, e a resposta diz quantas foram, em vez de esconder.
 *
 * ── De quem são os números ────────────────────────────────────────────────
 *
 * `abrangencia` é a carteira de quem pergunta, como no painel: o gestor de uma
 * unidade vê o ranking da unidade. Venda sem responsável não tem pódio, e só o
 * escopo da organização inteira a enxerga.
 */

import { z } from 'zod';
import { IdSchema } from './comum.js';
import { AbrangenciaSchema } from './dashboard.js';
import { InstanteSchema } from './valores.js';

/** `YYYY-MM-DD` no fuso da revenda. */
const DiaSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

const PessoaDoRankingSchema = z.object({
  /** A pessoa no CliqCRM; nulo quando o responsável não tem vínculo nesta organização. */
  usuario_id: IdSchema.nullable(),
  /** O responsável como aparece na oportunidade — é o que o detalhamento recebe. */
  responsavel_referencia_externa: z.string(),
  nome: z.string().nullable(),
  foto_url: z.string().nullable(),
});

export const PosicaoNoRankingSchema = PessoaDoRankingSchema.extend({
  /** 1 = maior valor vendido. Empate no valor desempata pela quantidade, depois pelo nome. */
  posicao: z.number().int().positive(),
  valor_micros: z.number(),
  quantidade: z.number().int(),
  /** Valor vendido dividido pelas vendas COM valor; zero se nenhuma tem valor. */
  ticket_medio_micros: z.number(),
  /** A posição no período anterior de mesmo tamanho; nula se não vendeu nele. */
  posicao_anterior: z.number().int().positive().nullable(),
});

export const RankingDeVendasSchema = z.object({
  periodo: z.object({ de: DiaSchema, ate: DiaSchema, fuso: z.string() }),
  /** O período imediatamente anterior, do mesmo tamanho: é a base da variação de posição. */
  periodo_anterior: z.object({ de: DiaSchema, ate: DiaSchema }),
  abrangencia: AbrangenciaSchema,
  /** A moeda dos valores. Hoje o cadastro guarda tudo em uma moeda por organização. */
  moeda: z.string(),
  itens: z.array(PosicaoNoRankingSchema),
  /** Quem está no escopo e não vendeu no período. Aparece no fim, sem posição. */
  sem_vendas: z.array(PessoaDoRankingSchema),
  /**
   * Vendas cuja oportunidade foi apagada ou ainda não chegou ao CliqCRM. Não
   * têm responsável conhecido, e por isso não entram em lugar nenhum do ranking.
   */
  vendas_sem_oportunidade: z.number().int(),
  /** Vendas do período sem valor preenchido: contam na quantidade e somam zero. */
  vendas_sem_valor: z.number().int(),
  /** Vendas sem responsável. Só a abrangência da organização inteira as enxerga. */
  sem_responsavel: z.object({ quantidade: z.number().int(), valor_micros: z.number() }),
  /** O que os filtros da tela podem oferecer, já dentro do escopo de quem pergunta. */
  filtros: z.object({
    funis: z.array(z.object({ chave: z.string(), nome: z.string() })),
    times: z.array(z.object({ id: IdSchema, nome: z.string() })),
  }),
});

export const VendaDoRankingSchema = z.object({
  /** O id canônico da oportunidade; nulo se ela ainda não foi vista pelo CliqCRM. */
  oportunidade_id: IdSchema.nullable(),
  referencia_externa: z.string(),
  titulo: z.string().nullable(),
  valor_micros: z.number().nullable(),
  moeda: z.string().nullable(),
  ganha_em: InstanteSchema,
  funil: z.string(),
  funil_nome: z.string(),
});

export const VendasDoRankingSchema = z.object({
  itens: z.array(VendaDoRankingSchema),
});

export type RankingDeVendas = z.infer<typeof RankingDeVendasSchema>;
export type PosicaoNoRanking = z.infer<typeof PosicaoNoRankingSchema>;
export type VendaDoRanking = z.infer<typeof VendaDoRankingSchema>;

// ── Comparativos ───────────────────────────────────────────────────────────

/** Contra o quê o período é comparado. */
export const BaseDeComparacaoSchema = z.enum(['anterior', 'mes_anterior', 'ano_anterior']);

export const NumeroComparadoSchema = z.object({
  chave: z.string(),
  nome: z.string(),
  grupo: z.enum(['atendimento', 'comercial']),
  /** Como a tela escreve o número: contagem, dinheiro em micros ou duração em segundos. */
  formato: z.enum(['numero', 'dinheiro', 'duracao']),
  atual: z.number().nullable(),
  base: z.number().nullable(),
  /** Variação em porcentagem; nula quando a base é zero ou falta um dos dois. */
  variacao_percentual: z.number().nullable(),
  /** Se subir é bom ou ruim: espera e sem resposta melhoram quando caem. */
  melhor_quando: z.enum(['maior', 'menor']),
  abrangencia: AbrangenciaSchema,
});

export const ComparativosSchema = z.object({
  periodo: z.object({ de: DiaSchema, ate: DiaSchema, fuso: z.string() }),
  base: z.object({ tipo: BaseDeComparacaoSchema, de: DiaSchema, ate: DiaSchema }),
  moeda: z.string(),
  itens: z.array(NumeroComparadoSchema),
});

// ── Gargalos do funil ─────────────────────────────────────────────────────

export const EtapaComGargaloSchema = z.object({
  etapa: z.string(),
  nome: z.string(),
  /** Oportunidades na etapa agora. */
  em_aberto: z.number().int(),
  /** Destas, as paradas na etapa há `parada_dias` ou mais. */
  paradas: z.number().int(),
  /** Há quantos dias está ali a mais antiga; nulo com a etapa vazia. */
  mais_antiga_dias: z.number().int().nullable(),
  /** Quantas entraram na etapa no período. */
  entradas: z.number().int(),
  /** Das que entraram, quantas já saíram para uma etapa posterior do funil. */
  avancaram: z.number().int(),
  /** Tempo médio na etapa, em horas, das que entraram no período e já saíram. */
  tempo_medio_horas: z.number().nullable(),
});

export const GargalosDoFunilSchema = z.object({
  periodo: z.object({ de: DiaSchema, ate: DiaSchema, fuso: z.string() }),
  abrangencia: AbrangenciaSchema,
  funil: z.object({ chave: z.string(), nome: z.string() }).nullable(),
  funis: z.array(z.object({ chave: z.string(), nome: z.string() })),
  /** A partir de quantos dias na mesma etapa uma oportunidade conta como parada. */
  parada_dias: z.number().int(),
  etapas: z.array(EtapaComGargaloSchema),
  /** Quem tem oportunidade parada, de quem tem mais para quem tem menos. */
  responsaveis: z.array(
    z.object({
      usuario_id: IdSchema.nullable(),
      responsavel_referencia_externa: z.string().nullable(),
      nome: z.string().nullable(),
      foto_url: z.string().nullable(),
      paradas: z.number().int(),
    }),
  ),
});

export const OportunidadeParadaSchema = z.object({
  oportunidade_id: IdSchema.nullable(),
  referencia_externa: z.string(),
  titulo: z.string().nullable(),
  etapa: z.string(),
  etapa_nome: z.string(),
  dias_na_etapa: z.number().int(),
  valor_micros: z.number().nullable(),
  moeda: z.string().nullable(),
  responsavel_nome: z.string().nullable(),
});

export const OportunidadesParadasSchema = z.object({
  itens: z.array(OportunidadeParadaSchema),
});

// ── Atendimento ───────────────────────────────────────────────────────────

export const AtendimentoDoPeriodoSchema = z.object({
  periodo: z.object({ de: DiaSchema, ate: DiaSchema, fuso: z.string() }),
  /**
   * O volume é da organização inteira, como no painel: a conversa guarda o id
   * de agente do atendimento, e só o bloco por pessoa se recorta pela carteira.
   */
  abrangencia_do_volume: AbrangenciaSchema,
  resumo: z.object({
    atendimentos: z.number().int(),
    resolvidas: z.number().int(),
    sem_resposta: z.number().int(),
    /** Mediana das medianas diárias: serve para comparar períodos, não para auditar um caso. */
    espera_mediana_segundos: z.number().nullable(),
    espera_p90_segundos: z.number().nullable(),
  }),
  por_canal: z.array(
    z.object({
      canal: z.string(),
      atendimentos: z.number().int(),
      mensagens_recebidas: z.number().int(),
      espera_mediana_segundos: z.number().nullable(),
    }),
  ),
  /**
   * Quando os clientes escrevem: mensagens recebidas em cada hora de cada dia
   * da semana (0 = domingo), no fuso da organização. É a demanda, e é o que
   * responde "em que horário preciso de mais gente".
   */
  por_hora: z.array(
    z.object({
      dia_semana: z.number().int().min(0).max(6),
      hora: z.number().int().min(0).max(23),
      mensagens_recebidas: z.number().int(),
    }),
  ),
  por_pessoa: z.object({
    abrangencia: AbrangenciaSchema,
    itens: z.array(
      z.object({
        usuario_id: IdSchema,
        nome: z.string().nullable(),
        foto_url: z.string().nullable(),
        /** Conversas que começaram no período e estão com a pessoa agora. */
        conversas: z.number().int(),
        /** Mensagens que a pessoa enviou pelo CliqCRM no período. */
        mensagens_enviadas: z.number().int(),
      }),
    ),
  }),
});

export type Comparativos = z.infer<typeof ComparativosSchema>;
export type NumeroComparado = z.infer<typeof NumeroComparadoSchema>;
export type BaseDeComparacao = z.infer<typeof BaseDeComparacaoSchema>;
export type GargalosDoFunil = z.infer<typeof GargalosDoFunilSchema>;
export type EtapaComGargalo = z.infer<typeof EtapaComGargaloSchema>;
export type OportunidadeParada = z.infer<typeof OportunidadeParadaSchema>;
export type AtendimentoDoPeriodo = z.infer<typeof AtendimentoDoPeriodoSchema>;
